import { useCallback, useEffect, useState } from "react";

export type MapCircleParams = {
  map?: google.maps.Map;
  center?: google.maps.LatLngLiteral;
  radius?: number;
  onCircleUpdate?: (params: { center: google.maps.LatLngLiteral; radius: number }) => void;
};

export const useMapCircle = ({ map, radius, center, onCircleUpdate }: MapCircleParams) => {
  const [circle, setCircle] = useState<google.maps.Circle | null>(null);

  const fitCircleInBounds = useCallback(
    (circle: google.maps.Circle) => {
      const circleBounds = circle.getBounds();
      map?.fitBounds(circleBounds);
      map?.panToBounds(circleBounds);
    },
    [map]
  );

  useEffect(() => {
    let centerChangedListener: google.maps.MapsEventListener | null = null;
    let radiusChangedListener: google.maps.MapsEventListener | null = null;

    const handleCircleChangeListeners = (
      circle: google.maps.Circle,
      center: google.maps.LatLngLiteral,
      radius: number
    ) => {
      centerChangedListener = google.maps.event.addListener(circle, "center_changed", () => {
        onCircleUpdate?.({
          center: circle.getCenter().toJSON(),
          radius,
        });
      });
      radiusChangedListener = google.maps.event.addListener(circle, "radius_changed", () => {
        onCircleUpdate?.({
          center,
          radius: circle.getRadius(),
        });
      });
    };

    if (!(map && center && radius)) {
      circle?.setMap(null);
      setCircle(null);
      if (centerChangedListener) {
        google.maps.event.removeListener(centerChangedListener);
      }
      if (radiusChangedListener) {
        google.maps.event.removeListener(radiusChangedListener);
      }
      return;
    }

    if (!circle) {
      const newCircle = new google.maps.Circle({
        strokeColor: "#0284C7",
        strokeOpacity: 1,
        strokeWeight: 2,
        fillColor: "#D8D8D8",
        fillOpacity: 0.2,
        map,
        center,
        radius,
        editable: true,
        suppressUndo: true,
      } as google.maps.CircleOptions);
      handleCircleChangeListeners(newCircle, center, radius);
      setCircle(newCircle);
      fitCircleInBounds(newCircle);
    } else {
      handleCircleChangeListeners(circle, center, radius);
      circle.setCenter(center);
      circle.setRadius(radius);
      fitCircleInBounds(circle);
    }
    return () => {
      if (centerChangedListener) {
        google.maps.event.removeListener(centerChangedListener);
      }
      if (radiusChangedListener) {
        google.maps.event.removeListener(radiusChangedListener);
      }
    };
  }, [map, center, radius, onCircleUpdate, fitCircleInBounds, circle]);
};
